import { concat, get } from 'lodash';
import { COLLECTIONS } from 'src/constants/constants';
import { type ApiResponse, type ICCAsset, CCApiResponse } from 'src/interfaces';
import {
  IProduct,
  IProductCategory,
  IProductVariation,
  IProductVariationFlavor,
  IProductVariationSize,
  IVariationAsset,
} from 'src/interfaces/Product';
import { requestCCApi } from 'src/utils';

import { fetchProducts } from './useGetProducts';

export const mapProductsWithVariation = (
  response: CCApiResponse,
  categories: IProductCategory[],
): IProduct[] => {
  const products = get<IProduct[]>(response.includes, 'products.data', []);
  const productVariationTypes = get<IProductVariationSize[]>(
    response.includes,
    'productVariationSizes.data',
    [],
  );

  const productVariationFlavors = get<IProductVariationFlavor[]>(
    response.includes,
    'productVariationFlavors.data',
    [],
  );

  const allProducts = response.data.reduce(
    (acc: IProduct[], productVariation: IProductVariation) => {
      return acc.map((item) => {
        const product = { ...item };

        if (product.product_code === productVariation.group_product_code) {
          productVariation.productVariationSize = productVariationTypes.find(
            (variation) =>
              variation.id === productVariation.product_variation_size_id,
          );

          productVariation.productVariationFlavor =
            productVariationFlavors.find(
              (flavor) =>
                flavor.id === productVariation.product_variation_flavor_id,
            );

          product.productVariations = concat(product.productVariations ?? [], [
            productVariation,
          ]);

          product.productCategories = categories.filter((item) =>
            product.product_category_id.includes(item.id),
          );
        }
        if (product.product_code === productVariation.product_code) {
          product.product_name = productVariation.name;
        }

        return product;
      });
    },
    products,
  );

  return allProducts;
};

export const mapProductsWithCategories = (
  response: CCApiResponse,
  categories: IProductCategory[],
): IProduct[] => {
  let products = response.data.map((product: any) => {
    product.productCategories = categories.filter((item) =>
      product.product_category_id.includes(item.id),
    );

    return product;
  });

  return products;
};

export const mapProductVariationSizes = (
  variations: IProductVariation[],
  variationSizes: IProductVariationSize[],
  variationFlavors: IProductVariationFlavor[],
) => {
  return variations.map(
    (variation) =>
      ({
        ...variation,
        productVariationSize: variationSizes.find(
          (size) => size.id === variation.product_variation_size_id,
        ),
        productVariationFlavor: variationFlavors.find(
          (flavor) => flavor.id === variation.product_variation_flavor_id,
        ),
      } as IProductVariation),
  );
};

export const mapProducts = (
  products: IProduct[],
  productVariationsResponse: ApiResponse,
  productCategories: IProductCategory[],
  ccAssets?: ICCAsset[],
) => {
  const mappedProductVariations = productVariationsResponse.data.map(
    (productVariation: IProductVariation) =>
      mapProductVariation(
        productVariation,
        get(
          productVariationsResponse,
          'includes.productVariationSizes.data',
          [],
        ),
        get(
          productVariationsResponse,
          'includes.productVariationFlavors.data',
          [],
        ),
        get(productVariationsResponse, 'includes.assets.data', []),
      ),
  );

  return products.map((product) =>
    mapProduct(
      product,
      productCategories,
      mappedProductVariations,
      ccAssets ?? [],
    ),
  );
};

export const mapProduct = (
  product: IProduct,
  productCategories: IProductCategory[],
  productVariations: IProductVariation[],
  ccAssets: ICCAsset[],
): IProduct => {
  const tempProductVariation = productVariations.find(
    (item) => item.product_code === product.product_code,
  );
  const productName = tempProductVariation?.name ?? '';
  const hasAssignedComponent = tempProductVariation?.has_assigned_component;

  return {
    ...product,
    product_name: productName,
    has_assigned_component: hasAssignedComponent,
    productVariations: productVariations.filter(
      (productVariation) =>
        productVariation.group_product_code === product.product_code,
    ),
    productCategories: productCategories.filter((item) =>
      product.product_category_id.includes(item.id),
    ),
    assets: ccAssets.filter((asset) => product.asset_id.includes(asset.id)),
  };
};

export const mapProductVariation = (
  productVariation: IProductVariation,
  productVariationSizes: IProductVariationSize[],
  productVariationFlavors: IProductVariationFlavor[],
  ccAssets: ICCAsset[],
) => {
  return {
    ...productVariation,
    productVariationSize: productVariationSizes.find(
      (size) => size.id === productVariation.product_variation_size_id,
    ),
    productVariationFlavor: productVariationFlavors.find(
      (flavor) => flavor.id === productVariation.product_variation_flavor_id,
    ),
    assets: ccAssets.filter((asset) =>
      productVariation.asset_id.includes(asset.id),
    ),
  };
};

export const updateProductVariationsWithAssets = async (
  product: IProduct & { asset?: File } & {
    allVariationAssets: IVariationAsset[];
  },
) => {
  const variationAssetAPITransactionData: any[] = [];
  const updateAssetThemeTransaction: any[] = [];
  const assetsToMap: { asset: any; index: number }[] = [];

  const updatedProductVariations = product?.productVariations.map(
    (variation) => {
      const updatedVariationAssets = variation?.variationAssets?.map(
        (asset) => {
          const existingAsset = product?.allVariationAssets?.find(
            (allAsset) => allAsset.name === asset.assetName,
          );

          if (existingAsset) {
            const hasChanges =
              existingAsset.theme_id.length !== asset.themeIds?.length ||
              !existingAsset?.theme_id?.every((id) =>
                asset.themeIds?.includes(id),
              );
            if (hasChanges) {
              // Create a PUT request to update the existing asset
              updateAssetThemeTransaction.push({
                resource: COLLECTIONS.ASSETS,
                id: existingAsset.id,
                method: 'PUT',
                data: {
                  theme_id: asset.themeIds ?? [],
                  name: asset.assetName,
                },
              });
            }

            return { ...asset, assetId: existingAsset.id };
          } else {
            variationAssetAPITransactionData.push({
              resource: COLLECTIONS.ASSETS,
              method: 'POST',
              data: {
                name: asset.assetName,
                theme_id: asset.themeIds ?? [],
              },
            });
            assetsToMap.push({
              asset: { ...asset },
              index: variationAssetAPITransactionData.length - 1,
            });

            return asset;
          }
        },
      );

      return {
        ...variation,
        variationAssets: updatedVariationAssets,
      };
    },
  );

  if (variationAssetAPITransactionData?.length > 0) {
    const response = await requestCCApi({
      url: '/transaction',
      method: 'POST',
      data: { data: [...variationAssetAPITransactionData] },
    });

    const responseIds = response.results;

    assetsToMap?.forEach(({ asset, index }) => {
      if (responseIds[index] !== true) {
        asset.assetId = responseIds[index];
      }
    });
  }

  const finalProductVariations = updatedProductVariations.map((variation) => {
    const updatedVariationAssets = variation.variationAssets?.map((asset) => {
      const assetCopy = { ...asset };
      const mappedAsset = assetsToMap.find(
        ({ asset: mapAsset }) => mapAsset.assetName === assetCopy.assetName,
      );

      if (mappedAsset) {
        assetCopy.assetId = mappedAsset.asset.assetId;
        assetCopy.isDY = mappedAsset.asset.isDY;
      }

      return assetCopy;
    });

    const assetIds = updatedVariationAssets?.map((asset) => asset.assetId);
    const dyAsset = updatedVariationAssets?.find((asset) => asset.isDY);

    return {
      ...variation,
      variationAssets: updatedVariationAssets,
      asset_id: assetIds,
      dy_asset: dyAsset?.assetId ?? null,
    };
  });

  return { finalProductVariations, updateAssetThemeTransaction };
};

export const validateProductCode = async (code: string) => {
  const products = await fetchProducts([], [`product_code.list=${code}`]);
  const existsInProducts = products.some((p) => p.product_code === code);

  const existsInVariations = products.some((p) =>
    p.productVariations?.some((v) => v.product_code === code),
  );

  return existsInProducts || existsInVariations;
};

export const getAssetIds = (productData?: IProduct | null): string[] => {
  if (!productData) return [];

  return Array.from(
    new Set(
      [
        ...(productData.asset_id ?? []),
        ...(productData.productVariations?.flatMap((v) => v.asset_id ?? []) ??
          []),
        ...(productData.productVariations?.flatMap(
          (v) => v.variationAssets?.map((a) => a.assetId) ?? [],
        ) ?? []),
      ]
        .filter((id) => id !== undefined && id !== null)
        .map((id) => String(id)),
    ),
  );
};
